import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  EmployerLead,
  InteractionLog,
  LeadTask,
  PilotLeadStatus,
  PilotLeadSource,
  PilotLeadPriority,
} from '@/models/pilot-crm.model';
import { PILOT_LEAD_STATUSES } from '@/data/pilot-crm-constants';

const PILOT_CRM_LEADS_COLLECTION = 'pilotCrmLeads';
const PILOT_CRM_INTERACTIONS_COLLECTION = 'pilotCrmInteractionLogs';
const PILOT_CRM_TASKS_COLLECTION = 'pilotCrmTasks';

export interface PipelineMetrics {
  byStatus: Record<PilotLeadStatus, number>;
  bySource: Record<PilotLeadSource, number>;
  byPriority: Record<PilotLeadPriority, number>;
  totalLeads: number;
  totalValue: number;
}

export interface ConversionMetrics {
  overallConversion: number;
  stageConversions: Record<string, number>;
  avgTimeInStage: Record<PilotLeadStatus, number>;
}

export interface ActivityMetrics {
  emailsSent: number;
  callsMade: number;
  meetingsHeld: number;
  notesAdded: number;
  tasksCompleted: number;
  tasksOpen: number;
}

export interface LeadVelocityPoint {
  date: string;
  newLeads: number;
  liveLeads: number;
  totalActive: number;
}

export interface DateRange {
  start: Date;
  end: Date;
}

const EMPLOYEE_VALUE_MAP: Record<string, number> = {
  '1-9': 5000,
  '10-49': 15000,
  '50-199': 50000,
  '200-999': 150000,
  '1000+': 500000,
};

const PIPELINE_STAGES: PilotLeadStatus[] = [
  'new',
  'invited',
  'replied',
  'call_booked',
  'accepted',
  'onboarding',
  'live',
];

const TERMINAL_STAGES: PilotLeadStatus[] = ['not_fit', 'paused', 'no_response'];

export class PilotCrmAnalyticsService {
  private static docToLead(id: string, data: any): EmployerLead {
    return {
      id,
      companyName: data.companyName || '',
      industry: data.industry || undefined,
      employeeCountRange: data.employeeCountRange || undefined,
      website: data.website || undefined,
      hqState: data.hqState || undefined,
      contactName: data.contactName || undefined,
      contactEmail: data.contactEmail || '',
      contactRole: data.contactRole || undefined,
      phone: data.phone || undefined,
      googlePlaceId: data.googlePlaceId || undefined,
      rating: data.rating || undefined,
      reviewCount: data.reviewCount || undefined,
      address: data.address || undefined,
      city: data.city || undefined,
      state: data.state || undefined,
      zipCode: data.zipCode || undefined,
      placeUrl: data.placeUrl || undefined,
      source: data.source || 'other',
      status: data.status || 'new',
      priority: data.priority || 'normal',
      tags: data.tags || [],
      notes: data.notes || undefined,
      nextActionAt: data.nextActionAt?.toDate(),
      ownerUserId: data.ownerUserId || undefined,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      createdBy: data.createdBy || 'system',
      updatedBy: data.updatedBy || 'system',
    };
  }

  private static docToInteraction(id: string, data: any): InteractionLog {
    return {
      id,
      leadId: data.leadId,
      type: data.type || 'note',
      channel: data.channel || 'in_app',
      subject: data.subject || undefined,
      summary: data.summary || '',
      raw: data.raw || undefined,
      createdAt: data.createdAt?.toDate() || new Date(),
      createdBy: data.createdBy || 'system',
    };
  }

  private static docToTask(id: string, data: any): LeadTask {
    return {
      id,
      leadId: data.leadId,
      title: data.title || '',
      type: data.type || 'other',
      dueAt: data.dueAt?.toDate() || new Date(),
      status: data.status || 'open',
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      createdBy: data.createdBy || 'system',
      updatedBy: data.updatedBy || 'system',
    };
  }

  static async getAllLeads(dateRange?: DateRange): Promise<EmployerLead[]> {
    let q = query(
      collection(db, PILOT_CRM_LEADS_COLLECTION),
      orderBy('createdAt', 'desc')
    );

    if (dateRange) {
      q = query(
        collection(db, PILOT_CRM_LEADS_COLLECTION),
        where('createdAt', '>=', Timestamp.fromDate(dateRange.start)),
        where('createdAt', '<=', Timestamp.fromDate(dateRange.end)),
        orderBy('createdAt', 'desc')
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((docSnap) => this.docToLead(docSnap.id, docSnap.data()));
  }

  static async getAllInteractions(dateRange?: DateRange): Promise<InteractionLog[]> {
    let q = query(
      collection(db, PILOT_CRM_INTERACTIONS_COLLECTION),
      orderBy('createdAt', 'desc')
    );

    if (dateRange) {
      q = query(
        collection(db, PILOT_CRM_INTERACTIONS_COLLECTION),
        where('createdAt', '>=', Timestamp.fromDate(dateRange.start)),
        where('createdAt', '<=', Timestamp.fromDate(dateRange.end)),
        orderBy('createdAt', 'desc')
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((docSnap) => this.docToInteraction(docSnap.id, docSnap.data()));
  }

  static async getAllTasks(dateRange?: DateRange): Promise<LeadTask[]> {
    let q = query(
      collection(db, PILOT_CRM_TASKS_COLLECTION),
      orderBy('createdAt', 'desc')
    );

    if (dateRange) {
      q = query(
        collection(db, PILOT_CRM_TASKS_COLLECTION),
        where('createdAt', '>=', Timestamp.fromDate(dateRange.start)),
        where('createdAt', '<=', Timestamp.fromDate(dateRange.end)),
        orderBy('createdAt', 'desc')
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((docSnap) => this.docToTask(docSnap.id, docSnap.data()));
  }

  static async getPipelineMetrics(dateRange?: DateRange): Promise<PipelineMetrics> {
    const leads = await this.getAllLeads(dateRange);

    const byStatus: Record<PilotLeadStatus, number> = {} as Record<PilotLeadStatus, number>;
    const bySource: Record<PilotLeadSource, number> = {} as Record<PilotLeadSource, number>;
    const byPriority: Record<PilotLeadPriority, number> = {} as Record<PilotLeadPriority, number>;

    PILOT_LEAD_STATUSES.forEach((status) => {
      byStatus[status] = 0;
    });

    let totalValue = 0;

    leads.forEach((lead) => {
      byStatus[lead.status] = (byStatus[lead.status] || 0) + 1;
      bySource[lead.source] = (bySource[lead.source] || 0) + 1;
      byPriority[lead.priority] = (byPriority[lead.priority] || 0) + 1;

      if (lead.employeeCountRange) {
        totalValue += EMPLOYEE_VALUE_MAP[lead.employeeCountRange] || 0;
      }
    });

    return {
      byStatus,
      bySource,
      byPriority,
      totalLeads: leads.length,
      totalValue,
    };
  }

  static async getConversionMetrics(dateRange?: DateRange): Promise<ConversionMetrics> {
    const leads = await this.getAllLeads(dateRange);

    const newLeads = leads.filter((l) => PIPELINE_STAGES.includes(l.status) || TERMINAL_STAGES.includes(l.status)).length;
    const liveLeads = leads.filter((l) => l.status === 'live').length;
    const overallConversion = newLeads > 0 ? (liveLeads / newLeads) * 100 : 0;

    const stageConversions: Record<string, number> = {};
    for (let i = 0; i < PIPELINE_STAGES.length - 1; i++) {
      const fromStage = PIPELINE_STAGES[i];
      const toStage = PIPELINE_STAGES[i + 1];
      const fromCount = leads.filter((l) => {
        const stageIndex = PIPELINE_STAGES.indexOf(l.status);
        return stageIndex >= i || TERMINAL_STAGES.includes(l.status);
      }).length;
      const toCount = leads.filter((l) => {
        const stageIndex = PIPELINE_STAGES.indexOf(l.status);
        return stageIndex >= i + 1;
      }).length;
      stageConversions[`${fromStage}_to_${toStage}`] = fromCount > 0 ? (toCount / fromCount) * 100 : 0;
    }

    const avgTimeInStage: Record<PilotLeadStatus, number> = {} as Record<PilotLeadStatus, number>;
    PILOT_LEAD_STATUSES.forEach((status) => {
      avgTimeInStage[status] = 0;
    });

    return {
      overallConversion,
      stageConversions,
      avgTimeInStage,
    };
  }

  static async getActivityMetrics(dateRange?: DateRange, userId?: string): Promise<ActivityMetrics> {
    let interactions = await this.getAllInteractions(dateRange);
    let tasks = await this.getAllTasks(dateRange);

    if (userId) {
      interactions = interactions.filter((i) => i.createdBy === userId);
      tasks = tasks.filter((t) => t.createdBy === userId);
    }

    return {
      emailsSent: interactions.filter((i) => i.type === 'email_sent').length,
      callsMade: interactions.filter((i) => i.type === 'call').length,
      meetingsHeld: interactions.filter((i) => i.type === 'meeting').length,
      notesAdded: interactions.filter((i) => i.type === 'note').length,
      tasksCompleted: tasks.filter((t) => t.status === 'done').length,
      tasksOpen: tasks.filter((t) => t.status === 'open').length,
    };
  }

  static async getLeadVelocity(days: number = 30): Promise<LeadVelocityPoint[]> {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const leads = await this.getAllLeads({ start: startDate, end: endDate });

    const velocityMap = new Map<string, { newLeads: number; liveLeads: number; totalActive: number }>();

    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateKey = d.toISOString().split('T')[0];
      velocityMap.set(dateKey, { newLeads: 0, liveLeads: 0, totalActive: 0 });
    }

    leads.forEach((lead) => {
      const createdDateKey = lead.createdAt.toISOString().split('T')[0];
      if (velocityMap.has(createdDateKey)) {
        const entry = velocityMap.get(createdDateKey)!;
        entry.newLeads += 1;
        if (!TERMINAL_STAGES.includes(lead.status)) {
          entry.totalActive += 1;
        }
        if (lead.status === 'live') {
          entry.liveLeads += 1;
        }
      }
    });

    const result: LeadVelocityPoint[] = [];
    velocityMap.forEach((value, key) => {
      result.push({
        date: key,
        newLeads: value.newLeads,
        liveLeads: value.liveLeads,
        totalActive: value.totalActive,
      });
    });

    return result.sort((a, b) => a.date.localeCompare(b.date));
  }

  static async getRecentActivity(limit: number = 20): Promise<InteractionLog[]> {
    const q = query(
      collection(db, PILOT_CRM_INTERACTIONS_COLLECTION),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs
      .slice(0, limit)
      .map((docSnap) => this.docToInteraction(docSnap.id, docSnap.data()));
  }

  static async getUpcomingTasks(limit: number = 10): Promise<LeadTask[]> {
    const now = new Date();
    const q = query(
      collection(db, PILOT_CRM_TASKS_COLLECTION),
      where('status', '==', 'open'),
      orderBy('dueAt', 'asc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs
      .slice(0, limit)
      .map((docSnap) => this.docToTask(docSnap.id, docSnap.data()));
  }

  static getPipelineStages(): PilotLeadStatus[] {
    return PIPELINE_STAGES;
  }

  static getTerminalStages(): PilotLeadStatus[] {
    return TERMINAL_STAGES;
  }

  static formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  static formatPercentage(value: number): string {
    return `${value.toFixed(1)}%`;
  }
}
